import React, { useState, useEffect } from 'react';
import { withTranslation } from "react-i18next"
import { connect } from "react-redux"
import { withRouter } from "react-router-dom"
import toastr from "toastr"
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Row,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Form,
  FormGroup,
  Input,
  Label,
  Spinner
} from "reactstrap"
import { Add } from "@mui/icons-material"
import FormSelectCompany from "../../components/Common/form-select-company"
import { getEmpresasDoUsuario } from "../../helpers/utils"
import {
  apiBuscarMenuHierarquia,
  apiCriarMenu,
  apiEditarMenu,
  apiExcluirMenu,
  apiEditarAgrupamento,
  apiExcluirAgrupamento,
  apiBuscarIcone,
  apiMoverMenu,
  apiMoverAgrupamento
} from "../../helpers/backend_helper"
import { ConfirmationModal } from "./confirmation-modal"
import { TreeNode } from "./tree-node"
import { getDialogConfig } from "./get-dialog-config"
import { translationMenuManagement } from "./utils/translation"
import { AdicionarGruposModal } from "./adicionar-grupos-modal"
import FormInputFile from "../../components/Common/form-input-file"
import usePermission from "../../helpers/permission/usePermission"

const MenuManagement = (props) => {
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [menuData, setMenuData] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [currentDialog, setCurrentDialog] = useState(null);
  const [formValues, setFormValues] = useState({});
  const [formErrors, setFormErrors] = useState({});
  const [itemToDelete, setItemToDelete] = useState(null);
  const [expandedNodes, setExpandedNodes] = useState(new Set());

  const [loadingMenus, setLoadingMenus] = useState(false);
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [loadingDelete, setLoadingDelete] = useState(false);
  const [menuAdicionarGrupos, setMenuAdicionarGrupos] = useState(null);
  const [movendo, setMovendo] = useState(false);
  const translation = translationMenuManagement(props)

  const usePermissionEdit = usePermission("menuDispositivo_editar")
  const usePermissionInsert = usePermission("menuDispositivo_inserir")

  useEffect(() => {
    const empresasDoUsuario = getEmpresasDoUsuario(props.user, props.empresas);
    if (!empresasDoUsuario) return;

    const { opcoes, inicial } = empresasDoUsuario;
    setCompanies(opcoes);
    setSelectedCompany(atual =>
      opcoes.some(opcao => opcao.value === atual) ? atual : (inicial?.value || null));
  }, [props.user, props.empresas]);

  useEffect(() => {
    return () => {
      menuData.forEach(menu => {
        if (menu.icone && menu.icone.startsWith('blob:')) {
          URL.revokeObjectURL(menu.icone);
        }
      });
    };
  }, [menuData]);

  useEffect(() => {
    if (selectedCompany) {
      loadMenuHierarchy(selectedCompany);
    } else {
      setMenuData([]);
      setLoadingMenus(false);
    }
  }, [selectedCompany]);

  const loadMenuHierarchy = async (empresaId) => {
    try {
      setLoadingMenus(true);
      const response = await apiBuscarMenuHierarquia({ idEmpresa: empresaId })
      if(response.length === 0) setMenuData([])

      const formattedData = await transformApiDataToTree(response);
      setMenuData(formattedData);
    } catch (error) {
      toastr.error(translation.error);
      setMenuData([]);
    } finally {
      setLoadingMenus(false);
    }
  };

  const handleToggleNode = (nodeId, nodeType) => {
    const nodeKey = `${nodeType}_${nodeId}`;
    
    setExpandedNodes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(nodeKey)) {
        newSet.delete(nodeKey);
      } else {
        newSet.add(nodeKey);
      }

      return newSet;
    });

    setMenuData(prevData => {
      const updateNode = (nodes) => {
        return nodes.map(node => {
          if (node.id === nodeId && node.type === nodeType) {
            return { ...node, expanded: !node.expanded };
          }
          
          if (node.children && node.children.length > 0) {
            return {
              ...node,
              children: updateNode(node.children)
            };
          }
          
          return node;
        });
      };

      return updateNode(prevData);
    });
  };

  const transformApiDataToTree = async apiData => {
    const menus = await Promise.all(
      apiData.map(async menu => {
        let iconeUrl = null
        let iconeArquivo = null

        if (menu.icone) {
          try {
            const blob = await apiBuscarIcone({ idMenu: menu.id });

            if (blob && blob.size > 0) {
              iconeUrl = URL.createObjectURL(blob);
              iconeArquivo = blob;
            } else {
              console.warn(`Ícone do menu ${menu.id} retornou um blob inválido.`);
            }
          } catch (error) {
            console.error(`Erro ao buscar e processar o ícone para o menu ${menu.id}:`, error);
          }
        }

        return {
          id: menu.id,
          nome: menu.nome,
          icone: iconeUrl,
          iconeArquivo,
          ordem: menu.ordem,
          type: "menu",
          expanded: expandedNodes.has(`menu_${menu.id}`),
          children: (menu?.agrupamentos || []).map(grupo => ({
            id: grupo.id,
            nome: grupo.nome,
            ordem: grupo.ordem,
            grupo: grupo.grupo,
            type: "group",
            parentId: menu.id,
          })),
        }
      })
    )

    return menus
  }

  const handleCompanyChange = (companyId) => {
    setSelectedCompany(companyId);
  };


  const handleCreate = (parentNode) => {
    if (parentNode?.type === 'menu') {
      setMenuAdicionarGrupos(parentNode);
      return;
    }

    const config = getDialogConfig('menu');
    setCurrentDialog({ type: 'menu', mode: 'create', ...config });
    setFormValues({});
    setFormErrors({});
    setModalOpen(true);
  };

  const handleEdit = (node) => {
    const config = getDialogConfig(node.type);
    setCurrentDialog({
      type: node.type,
      node,
      mode: 'edit',
      ...config
    });

    const initialValues = { nome: node.nome };

    // O ícone já gravado entra só para a prévia; sem base64, o envio o deixa de fora
    if (node.type === 'menu' && node.iconeArquivo) {
      initialValues.icone = {
        nome: 'icone.png',
        file: node.iconeArquivo
      };
    }

    setFormValues(initialValues);
    setFormErrors({});
    setModalOpen(true);
  };

  const handleDelete = (node) => {
    setItemToDelete(node);
    setConfirmModalOpen(true);
  };

  const trocarComVizinho = (menus, node, direcao) => {
    const trocar = irmaos => {
      const origem = irmaos.findIndex(irmao => irmao.id === node.id);
      const destino = direcao === 'subir' ? origem - 1 : origem + 1;
      if (origem === -1 || destino < 0 || destino >= irmaos.length) return irmaos;
      const trocados = [...irmaos];
      [trocados[origem], trocados[destino]] = [trocados[destino], trocados[origem]];
      return trocados;
    };

    if (node.type === 'menu') return trocar(menus);
    return menus.map(menu => (menu.id === node.parentId ? { ...menu, children: trocar(menu.children) } : menu));
  };

  const handleMove = async (node, direcao) => {
    if (movendo) return;
    setMovendo(true);
    try {
      if (node.type === 'menu') {
        await apiMoverMenu({ id: node.id, direcao });
      } else {
        await apiMoverAgrupamento({ idMenu: node.parentId, id: node.id, direcao });
      }
      // O backend já trocou a posição: repete a troca aqui em vez de recarregar a árvore inteira.
      setMenuData(anteriores => trocarComVizinho(anteriores, node, direcao));
      toastr.success(translation.moveSuccess);
    } catch (error) {
      toastr.error(error?.response?.data?.[0]?.message || translation.moveError);
    } finally {
      setMovendo(false);
    }
  };

  const confirmDelete = async () => {
    if (!itemToDelete) return;

    try {
      setLoadingDelete(true);

      if (itemToDelete.type === 'menu') {
        await apiExcluirMenu({ id: itemToDelete.id });

        setExpandedNodes(prev => {
          const newSet = new Set(prev);
          newSet.delete(itemToDelete.id);
          return newSet;
        });

      } else if (itemToDelete.type === 'group') {
        await apiExcluirAgrupamento({ idMenu: itemToDelete.parentId, id: itemToDelete.id });

        setExpandedNodes(prev => {
          const newSet = new Set(prev);
          newSet.delete(itemToDelete.id);
          return newSet;
        });
      }

      toastr.success(translation.confirmDelete);
      setConfirmModalOpen(false);
      setItemToDelete(null);

      await loadMenuHierarchy(selectedCompany);

    } catch (error) {
      const message = error?.response?.data?.[0]?.message || translation.dialogError;
      toastr.error(message);
    } finally {
      setLoadingDelete(false);
    }
  };

  const handleFormChange = (fieldName, value) => {
    setFormValues(prev => ({ ...prev, [fieldName]: value }));

    if (formErrors[fieldName]) {
      setFormErrors(prev => ({ ...prev, [fieldName]: null }));
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!currentDialog) return false;

    currentDialog.fields.forEach(field => {
      if (field.required && (!formValues[field.name] || formValues[field.name].toString().trim() === '')) {
        errors[field.name] = `${field.label} é obrigatório`;
      }
    });

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      setLoadingSubmit(true);

      const payload = { ...formValues };

      if (payload.icone && payload.icone.base64SemPrefixo) {
        payload.icone = payload.icone.base64SemPrefixo;
      } else if (payload.icone && payload.icone.remover && currentDialog.mode === 'edit') {
        payload.icone = null;
      } else {
        delete payload.icone;
      }

      if (currentDialog.mode === 'create') {
        payload.id_empresa = parseInt(selectedCompany);
        await apiCriarMenu(payload);
      } else if (currentDialog.type === 'menu') {
        payload.id_empresa = parseInt(selectedCompany);
        await apiEditarMenu({
          id: currentDialog.node.id,
          ...payload
        });
      } else {
        await apiEditarAgrupamento({
          idMenu: currentDialog.node.parentId,
          id: currentDialog.node.id,
          nome: payload.nome
        });
      }
      toastr.success(translation.confirmSuccess);

      setModalOpen(false);
      setCurrentDialog(null);

      await loadMenuHierarchy(selectedCompany);

    } catch (error) {
      const message = error?.response?.data?.[0]?.message || translation.saveError;
      toastr.error(message);
    } finally {
      setLoadingSubmit(false);
    }
  };

  const renderFormField = (field) => {

    const fieldProps = {
      id: field.name,
      name: field.name,
      value: formValues[field.name] || '',
      onChange: (e) => handleFormChange(field.name, e.target.value),
      className: formErrors[field.name] ? 'is-invalid' : '',
      placeholder: field.placeholder,
      disabled: loadingSubmit
    };

    // O FormInputFile desenha o próprio rótulo; um Label aqui duplicaria o espaço acima do campo.
    if (field.type === 'file') {
      return (
        <FormInputFile
          key={field.name}
          attribute={field.name}
          description={field.label}
          value={formValues[field.name]}
          setValue={(value) => handleFormChange(field.name, value)}
          removeHandeler={() => handleFormChange(field.name, { remover: true })}
          required={field.required}
          disabled={loadingSubmit}
          extension="image/png, image/jpeg"
          labelColumns={12}
        />
      );
    }

    return (
      <FormGroup key={field.name} className="mb-3">
        <Label for={field.name} className="form-label">
          {field.label}
          {field.required && <span className="text-danger"> *</span>}
        </Label>
        <Input {...fieldProps} type={field.type || 'text'} />

        {formErrors[field.name] && (
          <div className="invalid-feedback d-block">
            {formErrors[field.name]}
          </div>
        )}
      </FormGroup>
    );
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Row>
          <Col lg={12}>
            <Card>
              <CardHeader>
                <h4 className="card-title mb-0">{translation.title}</h4>
              </CardHeader>
              <CardBody>
                <Row className="mb-4">
                  <Col md={6}>
                    <FormSelectCompany
                      attribute="empresa"
                      options={companies.filter(c => c.value)}
                      value={selectedCompany}
                      setValue={handleCompanyChange}
                      description={translation.description}
                      placeholder={translation.placeholder}
                      required={true}
                    />
                  </Col>
                  <Col md={6} className="d-flex align-items-end">
                    <Button
                      color="primary"
                      onClick={() => handleCreate()}
                      className="mb-4"
                      disabled={!selectedCompany || loadingMenus || !usePermissionEdit || !usePermissionInsert}
                    >
                      <Add className="me-1" />{translation.menuAdd}
                    </Button>
                  </Col>
                </Row>

                <Card>
                  <CardHeader>
                    <h5 className="card-title mb-0">{translation.subtitle}</h5>
                  </CardHeader>
                  <CardBody className="p-0">
                    {loadingMenus ? (
                      <div className="text-center p-4">
                        <Spinner color="primary" />
                        <p className="mt-2 text-muted">{translation.spinner}</p>
                      </div>
                    ) : menuData.length === 0 ? (
                      <div className="text-center p-4">
                        <p className="text-muted mb-0">
                          {selectedCompany ? translation.notFound : translation.select}
                        </p>
                      </div>
                    ) : (
                      <div className="menu-tree">
                        {menuData.map((node, index) => (
                          <TreeNode
                            key={node.id}
                            node={node}
                            isFirst={index === 0}
                            isLast={index === menuData.length - 1}
                            onToggle={handleToggleNode}
                            onEdit={handleEdit}
                            onCreate={handleCreate}
                            onDelete={handleDelete}
                            onMove={handleMove}
                            loading={loadingDelete}
                            disabled={movendo || !usePermissionEdit || !usePermissionInsert}
                            translation={translation}
                          />
                        ))}
                      </div>
                    )}
                  </CardBody>
                </Card>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>

      <Modal
        isOpen={modalOpen}
        toggle={() => !loadingSubmit && setModalOpen(!modalOpen)}
        size="md"
      >
        <ModalHeader toggle={() => !loadingSubmit && setModalOpen(!modalOpen)}>
          {currentDialog?.mode === 'create' ? translation.create : translation.edit} {currentDialog?.title}
        </ModalHeader>
        <Form>
          <ModalBody>
            {currentDialog?.type === 'group' && currentDialog?.node?.grupo && (
              <FormGroup className="mb-3">
                <Label for="grupo-vinculado" className="form-label">{translation.groupLinked}</Label>
                <Input
                  id="grupo-vinculado"
                  value={`${currentDialog.node.grupo.Codigo} - ${currentDialog.node.grupo.Descricao}`}
                  readOnly
                  disabled
                />
              </FormGroup>
            )}
            {currentDialog?.fields?.map((field) => renderFormField(field))}
          </ModalBody>
          <ModalFooter>
            <Button
              color="secondary"
              onClick={() => setModalOpen(false)}
              disabled={loadingSubmit}
            >
              Cancelar
            </Button>
            <Button
              color="primary"
              onClick={handleSubmit}
              disabled={loadingSubmit || !usePermissionEdit || !usePermissionInsert}
            >
              {loadingSubmit ? (
                <>
                  <Spinner size="sm" className="me-1" />
                  Salvando...
                </>
              ) : (
                currentDialog?.mode === 'create' ? translation.create : translation.save
              )}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>

      <AdicionarGruposModal
        menu={menuAdicionarGrupos}
        toggle={() => setMenuAdicionarGrupos(null)}
        onSalvo={async () => {
          setMenuAdicionarGrupos(null);
          await loadMenuHierarchy(selectedCompany);
        }}
        translation={translation}
      />

      <ConfirmationModal
        isOpen={confirmModalOpen}
        toggle={() => !loadingDelete && setConfirmModalOpen(false)}
        title={translation.confirmDelete}
        message={`${translation.modalMessage}  "${itemToDelete?.nome}"?`}
        warning={
          itemToDelete?.children && itemToDelete.children.length > 0
            ? translation.modalWarning
            : null
        }
        onConfirm={confirmDelete}
        confirmText={translation.confirmDelete}
        cancelText={translation.modalCancelButton}
        loading={loadingDelete}
      />
    </React.Fragment>
  );
};

MenuManagement.propTypes = {};

const mapStateToProps = ({ Login, Profile }) => {
  const { user } = Login;
  const { empresas } = Profile;
  return { user, empresas };
};

export default withTranslation()(connect(mapStateToProps, {})(withRouter(MenuManagement)));